import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { TrendingUp, Video, FileText, Wifi, Code } from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#020402] text-[#ECFDF5]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-emerald-500/20">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img 
              src="/logo.png" 
              alt="Trading Alienígena Logo" 
              className="w-12 h-12 object-contain"
            />
            <h1 className="text-xl font-bold tracking-wider" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              TRADING ALIENÍGENA
            </h1>
          </div>
          <div className="flex space-x-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/login')}
              className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
              data-testid="header-login-btn"
            >
              Iniciar Sesión
            </Button>
            <Button 
              onClick={() => navigate('/register')}
              className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold uppercase tracking-wider alien-glow"
              data-testid="header-register-btn"
            >
              Registrarse
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="pt-32 pb-20 px-6 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(2,4,2,0.9), rgba(2,4,2,0.95)), url('https://images.unsplash.com/photo-1764258559619-9d7dde5f7a47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxmdXR1cmlzdGljJTIwZ3JlZW4lMjB0aWdlciUyMGxvZ28lMjBibGFjayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzcxMjQ1MjI4fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 
                className="text-5xl lg:text-7xl font-bold leading-tight"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
                data-testid="hero-title"
              >
                EVOLUCIONA TU EDUCACIÓN
                <span className="block text-emerald-500">Y RESULTADOS EN EL MERCADO FINANCIERO</span>
              </h2>
              <p className="text-lg text-emerald-100/80 leading-relaxed">
                Accede a señales profesionales de trading, análisis de mercado en tiempo real y contenido educativo exclusivo. Únete a nuestra comunidad de traders profesionales.
              </p>
              <div className="flex space-x-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/register')}
                  className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold uppercase tracking-wider text-base px-8 py-6 alien-glow-strong"
                  data-testid="hero-cta-btn"
                >
                  Comenzar Ahora
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/live')}
                  className="border-2 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-400 font-bold uppercase tracking-wider text-base px-8 py-6"
                  data-testid="hero-live-btn"
                >
                  <Wifi className="mr-2 h-5 w-5" />
                  VER EN VIVO
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="glass-panel rounded-lg p-8 alien-glow">
                <img 
                  src="https://images.unsplash.com/photo-1639825752750-5061ded5503b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxzdG9jayUyMHRyYWRpbmclMjBjaGFydCUyMGRhcmslMjBtb2RlJTIwZ3JlZW4lMjBjYW5kbGVzfGVufDB8fHx8MTc3MTI0NTIzMnww&ixlib=rb-4.1.0&q=85"
                  alt="Trading Chart"
                  className="w-full h-auto rounded"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-[#0a140a]">
        <div className="container mx-auto max-w-6xl">
          <h3 
            className="text-4xl font-bold text-center mb-16"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            NUESTRO ECOSISTEMA DE TRADING
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="glass-panel rounded-lg p-6 hover:border-emerald-500/50 transition-all duration-300" data-testid="feature-signals">
              <div className="w-12 h-12 bg-emerald-500/20 rounded flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-emerald-500" />
              </div>
              <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Señales Profesionales
              </h4>
              <p className="text-emerald-100/70">
                Señales de compra/venta con análisis detallado, precio de entrada, stop loss y take profit.
              </p>
            </div>

            <div className="glass-panel rounded-lg p-6 hover:border-emerald-500/50 transition-all duration-300" data-testid="feature-live">
              <div className="w-12 h-12 bg-emerald-500/20 rounded flex items-center justify-center mb-4">
                <Wifi className="h-6 w-6 text-emerald-500" />
              </div>
              <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Transmisiones en Vivo
              </h4>
              <p className="text-emerald-100/70">
                Análisis de mercado en tiempo real vía Twitch. Aprende directamente de profesionales.
              </p>
            </div>

            <div className="glass-panel rounded-lg p-6 hover:border-emerald-500/50 transition-all duration-300" data-testid="feature-videos">
              <div className="w-12 h-12 bg-emerald-500/20 rounded flex items-center justify-center mb-4">
                <Video className="h-6 w-6 text-emerald-500" />
              </div>
              <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Contenido Educativo
              </h4>
              <p className="text-emerald-100/70">
                Biblioteca completa de videos educativos sobre estrategias y análisis técnico.
              </p>
            </div>

            <div className="glass-panel rounded-lg p-6 hover:border-emerald-500/50 transition-all duration-300" data-testid="feature-resources">
              <div className="w-12 h-12 bg-emerald-500/20 rounded flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-emerald-500" />
              </div>
              <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Material de Apoyo
              </h4>
              <p className="text-emerald-100/70">
                Guías, PDFs y recursos descargables para mejorar tu trading.
              </p>
            </div>

            <div className="glass-panel rounded-lg p-6 hover:border-emerald-500/50 transition-all duration-300" data-testid="feature-algorithms">
              <div className="w-12 h-12 bg-emerald-500/20 rounded flex items-center justify-center mb-4">
                <Code className="h-6 w-6 text-emerald-500" />
              </div>
              <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Algoritmos
              </h4>
              <p className="text-emerald-100/70">
                Scripts exclusivos de TradingView que podrás utilizar por ser parte de nuestra comunidad.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 px-6 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(2,4,2,0.85), rgba(2,4,2,0.9)), url('https://images.unsplash.com/photo-1759170588866-23d9bf987844?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxmdXR1cmlzdGljJTIwZ3JlZW4lMjB0aWdlciUyMGxvZ28lMjBibGFjayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzcxMjQ1MjI4fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto max-w-4xl text-center">
          <h3 
            className="text-5xl font-bold mb-6"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            LISTO PARA EMPEZAR?
          </h3>
          <p className="text-xl text-emerald-100/80 mb-10">
            Únete a nuestra comunidad de traders y lleva tu trading al siguiente nivel.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold uppercase tracking-wider text-lg px-12 py-7 alien-glow-strong"
            data-testid="cta-register-btn"
          >
            Haz Parte Aquí
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-emerald-500/20 bg-[#020402]">
        <div className="container mx-auto text-center text-emerald-100/50">
          <p>&copy; 2025 Trading Alienígena Investments. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}